import WebWidget from "@aichat/ui/components/ui/web-widget/index";
import useLocalSocket from "./hooks/useLocalSocket";
import { env } from "./lib/env";
import { COOKIE_KEY, IFRAME_MESSAGES } from "./lib/constants";
import { WidgetConfig } from "@aichat/types/widget";
import { useMemo, useState, useEffect, useCallback } from "react";
import { getProcessedMessages } from "./utils/message.utils";
import { SocketProvider, useSocket } from "@aichat/ui/hooks/use-socket";
import useQueryParams from "./hooks/useQueryParams";

const socketUrl = env.socketUrl;
const transports = env.socketTransports;

interface IOptionAuth {
  [key: string]: string;
}

interface IOptions {
  transports: string[];
  forceNew: boolean;
  reconnectionDelay: number;
  auth: IOptionAuth;
}

type AIChatWidgetProps = {
  config: WidgetConfig;
  apiKey: string;
  organizationId: string;
  projectId: string;
  isPlayground?: boolean;
  isAlwaysOpen?: boolean;
  deepgramApiKey: string;
  heygenApiKey: string;
  userId: string | null;
};

function AIChatWidgetCore(
  props: AIChatWidgetProps & {
    setConversationMedium: (medium?: "audio") => void;
    conversationMedium?: "audio";
    isCookieSet: boolean;
    setIsCookieSet: (isCookieSet: boolean) => void;
    setOptionsNew: (options: IOptions) => void;
    optionsNew: IOptions;
  },
) {
  const {
    setConversationMedium,
    isCookieSet,
    conversationMedium,
    setOptionsNew,
    setIsCookieSet,
  } = props;
  const starterSuggestions = props.config.starterSuggestions ?? [];

  const ParentMessage = useCallback(
    (event: MessageEvent) => {
      const { type } = event.data;
      if (type === IFRAME_MESSAGES.cookieHasBeenSet && !isCookieSet) {
        const { cookieList } = event.data;
        const tokenKey =
          cookieList[
            `${COOKIE_KEY.tokenKey}-${props.organizationId}-${props.apiKey}`
          ] ?? "";
        const serverTokenKey = cookieList[COOKIE_KEY.serverKey] ?? "";
        const tokenValue = conversationMedium
          ? cookieList[`${tokenKey}-${conversationMedium}`]
          : (cookieList[tokenKey] ?? "");
        setOptionsNew({
          transports: transports.split(","),
          forceNew: true,
          reconnectionDelay: 500,
          auth: {
            [serverTokenKey]: tokenValue,
          },
        });
        setIsCookieSet(true);
      }
    },
    [conversationMedium, isCookieSet, props.apiKey, props.organizationId],
  );

  useEffect(() => {
    window.addEventListener("message", ParentMessage);

    return () => {
      window.removeEventListener("message", ParentMessage);
    };
  }, [ParentMessage]);

  const { socket: widgetSocket, isConnected } = useSocket();

  const {
    messageLoading,
    isLoading,
    chat,
    onSendMessage,
    onOpenChat,
    onCloseWelcome,
    onInterrupt,
    messages,
    customerSurvey,
    isStreaming,
    streamedMsgContent,
    socketErrorMsg,
    isVoiceBotSpeaking,
    isRecording,
    showWelcomeContainer,
    setIsRecording,
    voiceOverlay,
    setVoiceOverlay,
    onAudioComplete,
    onAvatarStartTalking,
    onAvatarStopTalking,
    submitCustomerSurvey,
    endLiveChat,
    liveChatState,
    updateMessages,
    uploadFile,
  } = useLocalSocket({
    widgetSocket,
    isConnected,
    conversationMedium,
    organizationId: props.organizationId,
    apiKey: props.apiKey,
    projectId: props.projectId,
    isPlayground: props.isPlayground,
  });

  useQueryParams({
    orgId: props.organizationId,
    projectId: props.projectId,
    apiKey: props.apiKey,
  });

  const isFirstChat = messages.length < 2;
  const combinedMessages = getProcessedMessages({
    streamedMsgContent,
    isFirstChat,
    starterSuggestions,
    messages,
    isStreaming,
  });

  if (isLoading) return;
  return (
    <WebWidget
      organizationId={props.organizationId}
      apiKey={props.apiKey}
      // handleCustomerTyping={handleCustomerTyping}
      projectId={props.projectId}
      uploadFile={uploadFile}
      chat={chat}
      socketError={socketErrorMsg}
      config={props.config}
      messages={combinedMessages}
      updateMessages={updateMessages}
      customerSurvey={customerSurvey}
      submitCustomerSurvey={submitCustomerSurvey}
      onSend={onSendMessage}
      onOpenChat={onOpenChat}
      isAlwaysOpen={props.isAlwaysOpen}
      onCloseWelcome={onCloseWelcome}
      isStreaming={isStreaming}
      messageLoading={messageLoading}
      deepgramApiKey={props.deepgramApiKey}
      conversationMedium={conversationMedium}
      setConversationMedium={setConversationMedium}
      onInterrupt={onInterrupt}
      isVoiceBotSpeaking={isVoiceBotSpeaking}
      isRecording={isRecording}
      setIsRecording={setIsRecording}
      showWelcomeContainer={showWelcomeContainer}
      voiceOverlay={voiceOverlay}
      hideVoiceOverlay={() => setVoiceOverlay(null)}
      heygenApiKey={props.heygenApiKey}
      onAudioComplete={onAudioComplete}
      streamedMsgContent={streamedMsgContent}
      onAvatarStartTalking={onAvatarStartTalking}
      onAvatarStopTalking={onAvatarStopTalking}
      endLiveChat={endLiveChat}
      liveChatState={liveChatState}
    />
  );
}

export default function AIChatWidget(props: AIChatWidgetProps) {
  const [conversationMedium, setConversationMedium] = useState<"audio">();
  const [optionsNew, setOptionsNew] = useState<IOptions>({
    transports: transports.split(","),
    forceNew: true,
    reconnectionDelay: 500,
    auth: {
      "": "",
    },
  });

  const [isCookieSet, setIsCookieSet] = useState(false);

  const options = useMemo(() => {
    // move Cookies.get into ParentMessage() to get cookie from iframe via postMessage
    return optionsNew;
  }, [conversationMedium, props.organizationId, props.apiKey, isCookieSet]);

  return (
    <SocketProvider
      url={socketUrl}
      organizationId={props.organizationId}
      apiKey={props.apiKey}
      options={options}
      onConnect={(socketInstance) => {
        socketInstance.emit("widget:join_room", {
          isPlayground: props.isPlayground || false,
          userId: props.userId || undefined,
        });
      }}
      projectId={props.projectId}
      conversationMedium={conversationMedium}
    >
      <AIChatWidgetCore
        conversationMedium={conversationMedium}
        setConversationMedium={setConversationMedium}
        isCookieSet={isCookieSet}
        setIsCookieSet={setIsCookieSet}
        optionsNew={optionsNew}
        setOptionsNew={setOptionsNew}
        {...props}
      />
    </SocketProvider>
  );
}
